var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from "react";
import { VImprovementIcon, VDeclineIcon, VBulletIcon } from "../../assets";
import { Box, Icon, ChakraBadge } from "../../ui";
/**
 * Badges are used to highlight an item's status for quick recognition.
 * Badge composes and `Chakra's Badge component` and `Chakra's Box component`, so you can pass all its props.
 *
 * See more on  **Chakra API** or directly in [Chakra's documentation](https://chakra-ui.com/docs/components/badge/usage)
 *
 * ```tsx
 * import { Badge, BadgeProps } from '@springcare/sh-component-library'
 *
 * <Badge>Label</Badge>
 * ```
 *
 * ## Verdant API (prop)
 * - [Verdant's badge specification](https://www.figma.com/file/SwgpvBvow9GJAmtmKbXVN2/Badge)
 *
 * ### Variant
 * - `high-emphasis`
 * - `medium-emphasis`
 *
 * ### Type (ordered from most to least important)
 * - trend: "improvement" | "decline" | element
 * - icon: element
 * - status: boolean
 * - text (default)
 *
 * ### Color Scheme
 * - "base"
 * - "positive"
 * - "info"
 * - "warning"
 * - "negative"
 * - "neutral"
 *
 * <br>
 * <details>
 * <summary>**Chakra API**</summary>
 * <br>
 * Button composes and `Chakra's Button component` and `Chakra's Box props or Style Props`
 * - [Chakra Badge Props](https://chakra-ui.com/docs/components/badge/props)
 * - [Chakra Style Props](https://chakra-ui.com/docs/styled-system/style-props)
 *
 * </details>
 *
 * @override
 * @param {BadgeProps} props badge props
 * @author Alejandro De la Cruz Tarin <adelacruz.ctr@springhealth.com>
 * @returns {React.ReactElement} Badge
 * @see _badgeTheme button theme definition using chakra
 * @version 1.0.0
 */
export var Badge = React.forwardRef(function (_a, ref) {
    var trend = _a.trend, icon = _a.icon, status = _a.status, children = _a.children, _b = _a.size, size = _b === void 0 ? "md" : _b, _c = _a.variant, variant = _c === void 0 ? "medium-emphasis" : _c, badgeProps = __rest(_a, ["trend", "icon", "status", "children", "size", "variant"]);
    var _d = useMemo(function () {
        if (trend) {
            if (trend === "improvement")
                return [null, _jsx(Icon, { as: VImprovementIcon, boxSize: "14px" })];
            if (trend === "decline")
                return [null, _jsx(Icon, { as: VDeclineIcon, boxSize: "14px" })];
            return [null, _jsx(Icon, { as: trend, boxSize: "14px" })];
        }
        if (icon)
            return [_jsx(Icon, { as: icon, boxSize: "14px", mx: "v-2" }), null];
        if (status)
            return [_jsx(Icon, { as: VBulletIcon, boxSize: "14px" }), null];
        return [null, null];
    }, [icon, trend]), leftIcon = _d[0], rightIcon = _d[1];
    return (_jsxs(ChakraBadge, __assign({ variant: "v-".concat(variant), size: "v-".concat(size), ref: ref }, badgeProps, { children: [leftIcon, _jsxs(Box, __assign({ px: "v-2" }, { children: [" ", children] })), rightIcon] })));
});
